/**
 * Custom element for a masonry layout, arranging items with specified or dynamic widths and heights.
 * Supports mobile and desktop configurations via data attributes.
 */
class MasonryLayout extends HTMLElement {
    /**
     * Number of units to divide the container width (100 corresponds to 1% increments).
     * @type {number}
     */
    N = 100;

    /**
     * Array tracking the top position of each unit.
     * @type {number[]}
     */
    topPositions = new Array(this.N).fill(0);

    /**
     * Maximum bottom position to set container height.
     * @type {number}
     */
    maxBottom = 0;

    /**
     * Cache for storing measured heights of items.
     * @type {Map<string, number>}
     */
    heightCache = new Map();

    /**
     * Creates an instance of MasonryLayout and initializes resize event listener.
     */
    constructor() {
        super();
        this.handleResize = () => this.applyLayout();
        window.addEventListener('resize', this.handleResize);
    }

    /**
     * Applies the masonry layout when the element is connected to the DOM.
     */
    connectedCallback() {
        this.applyLayout();
    }

    /**
     * Removes the resize event listener when the element is disconnected from the DOM.
     */
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
    }

    /**
     * Initializes the masonry state.
     * @returns {{N: number, topPositions: number[], maxBottom: number}} Object containing masonry configuration.
     */
    initializeMasonry() {
        return {
            N: this.N,
            topPositions: new Array(this.N).fill(0),
            maxBottom: 0
        };
    }

    /**
     * Retrieves width and height for an item based on mobile or desktop attributes.
     * Falls back to content height if no height is specified.
     * @param {HTMLElement} item - The masonry item element.
     * @param {number} index - Index of the item for caching.
     * @returns {{widthPercentage: number, height: number}} Object with width (in %) and height (in px).
     */
    getItemDimensions(item, index) {
        const mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint || '750');        
        const isMobile = window.innerWidth <= mobileBreakpoint;
        
        const widthAttr = isMobile ? 'widthMobile' : 'widthDesktop';
        const heightAttr = isMobile ? 'heightMobile' : 'heightDesktop';
        const fallbackWidthAttr = 'width';
        const fallbackHeightAttr = 'height';

        let widthPercentage = parseFloat(
            item.dataset[widthAttr] || item.dataset[fallbackWidthAttr] || '100'
        );
        if (isNaN(widthPercentage)) {
            widthPercentage = 100;
        }

        let height;
        if (item.dataset[heightAttr] || item.dataset[fallbackHeightAttr]) {
            height = parseFloat(item.dataset[heightAttr] || item.dataset[fallbackHeightAttr] || '0');
            if (isNaN(height)) {
                height = this.measureContentHeight(item, widthPercentage, index);
            }
        } else {
            height = this.measureContentHeight(item, widthPercentage, index);
        }

        return { widthPercentage, height };
    }

    /**
     * Measures the content height of an item by cloning it, with caching.
     * @param {HTMLElement} item - The masonry item element.
     * @param {number} widthPercentage - The width percentage to apply.
     * @param {number} index - Index of the item for caching.
     * @returns {number} The measured height in pixels.
     */
    measureContentHeight(item, widthPercentage, index) {
        const cacheKey = `${item.dataset.id || index}-${widthPercentage}`;
        if (this.heightCache.has(cacheKey)) {
            return this.heightCache.get(cacheKey) || 0;
        }

        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.width = this.offsetWidth + 'px';

        const clonedItem = item.cloneNode(true);
        if (!(clonedItem instanceof HTMLElement)) return 0;

        clonedItem.style.width = `${widthPercentage}%`;
        clonedItem.style.position = 'static';

        tempContainer.appendChild(clonedItem);
        document.body.appendChild(tempContainer);
        const height = clonedItem.offsetHeight;
        document.body.removeChild(tempContainer);

        this.heightCache.set(cacheKey, height);
        return height;
    }

    /**
     * Finds the best position for an item based on available space, optimized with min-heap approach.
     * @param {number[]} topPositions - Array of top positions for each unit, guaranteed to be filled with numbers.
     * @param {number} span - Number of units the item spans.
     * @returns {{bestI: number, minMaxTop: number}} Object with the best starting index and top position.
     */
    findBestPosition(topPositions = [], span) {
        let bestI = 0;
        let minMaxTop = Infinity;
        if (!topPositions.length) {
            return { bestI, minMaxTop };
        }

        for (let i = 0; i <= this.N - span; i++) {
            let maxTop = Math.max(...topPositions.slice(i, i + span));
            if (maxTop < minMaxTop) {
                minMaxTop = maxTop;
                bestI = i;
            }
        }
        return { bestI, minMaxTop };
    }

    /**
     * Applies CSS styles to an item using template literals.
     * @param {HTMLElement} item - The masonry item element.
     * @param {number} leftPercentage - Left position in percentage.
     * @param {number} top - Top position in pixels.
     * @param {number} widthPercentage - Width in percentage.
     * @param {number} height - Height in pixels.
     */
    applyStylesToItem(item, leftPercentage, top, widthPercentage, height) {
        item.style.position = 'absolute';
        item.style.left = `${leftPercentage}%`;
        item.style.top = `${top}px`;
        item.style.width = `${widthPercentage}%`;
        item.style.height = `${height}px`;
        item.style.visibility = 'visible';
    }

    /**
     * Updates the top positions for units occupied by an item.
     * @param {number[]} topPositions - Array of top positions for each unit.
     * @param {number} bestI - Starting index of the item.
     * @param {number} span - Number of units the item spans.
     * @param {number} bottom - Bottom position of the item in pixels.
     */
    updateTopPositions(topPositions, bestI, span, bottom) {
        for (let j = bestI; j < bestI + span; j++) {
            topPositions[j] = bottom;
        }
    }

    /**
     * Sets the container height based on the maximum bottom position.
     * @param {number} maxBottom - Maximum bottom position in pixels.
     */
    setContainerHeight(maxBottom) {
        this.style.height = `${maxBottom}px`;
    }

    /**
     * Applies the masonry layout to all items, with performance logging if debug is enabled.
     */
    applyLayout() {

        const masonry = this.initializeMasonry();
        const items = this.querySelectorAll('.masonry-item');

        items.forEach((item, index) => {
            if (!(item instanceof HTMLElement)) return;

            const { widthPercentage, height } = this.getItemDimensions(item, index);
            const span = Math.round(widthPercentage * this.N / 100);
            const { bestI, minMaxTop } = this.findBestPosition(masonry.topPositions, span);
            const leftPercentage = (bestI / this.N * 100);
            const top = minMaxTop;
            this.applyStylesToItem(item, leftPercentage, top, widthPercentage, height);
            const bottom = top + height;
            if (bottom > masonry.maxBottom) masonry.maxBottom = bottom;
            this.updateTopPositions(masonry.topPositions, bestI, span, bottom);
        });

        this.setContainerHeight(masonry.maxBottom);
    }
}

if (!customElements.get('masonry-layout')) {
    customElements.define('masonry-layout', MasonryLayout);
}

